import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FilePdf, ArrowUUpLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "../../lib/api";
import { SectionHeader, Money, useFocusHighlight, ConfirmDialog } from "../../components/shared";
import { DataTable } from "../../components/DataTable";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { SalesDocList } from "./SalesDocList";
import { DeletedInvoices } from "./DeletedInvoices";

export function PostedDocuments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sub = searchParams.get("sub") || "pinv";
  const setSub = (v) => { const p = new URLSearchParams(searchParams); p.set("sub", v); setSearchParams(p, { replace: true }); };
  return (
    <div>
      <SectionHeader title="Posted Documents" subtitle="Archives & posted records" />
      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="rounded-sm mb-5 flex-wrap h-auto gap-1">
          <TabsTrigger value="pinv" data-testid="tab-posted-invoices" className="rounded-sm">Posted Sales Invoices</TabsTrigger>
          <TabsTrigger value="pcr" data-testid="tab-posted-cr" className="rounded-sm">Posted CR/Adj Notes</TabsTrigger>
          <TabsTrigger value="ship" data-testid="tab-posted-shipments" className="rounded-sm">Posted Sales Shipments</TabsTrigger>
          <TabsTrigger value="rr" data-testid="tab-posted-return-receipts" className="rounded-sm">Posted Return Receipts</TabsTrigger>
          <TabsTrigger value="aq" data-testid="tab-quote-archives" className="rounded-sm">Sales Quote Archives</TabsTrigger>
          <TabsTrigger value="ao" data-testid="tab-order-archives" className="rounded-sm">Sales Order Archives</TabsTrigger>
          <TabsTrigger value="ar" data-testid="tab-return-archives" className="rounded-sm">Sales Return Order Archives</TabsTrigger>
          <TabsTrigger value="del" data-testid="tab-deleted-invoices" className="rounded-sm">Deleted Invoices</TabsTrigger>
        </TabsList>
        <TabsContent value="pinv"><SalesDocList docType="Invoice" variant="posted" /></TabsContent>
        <TabsContent value="pcr"><SalesDocList docType="CreditMemo" variant="posted" /></TabsContent>
        <TabsContent value="ship"><PostedShipments /></TabsContent>
        <TabsContent value="rr"><PostedReturnReceipts /></TabsContent>
        <TabsContent value="aq"><SalesDocList docType="Quote" variant="archive" /></TabsContent>
        <TabsContent value="ao"><SalesDocList docType="Order" variant="archive" /></TabsContent>
        <TabsContent value="ar"><SalesDocList docType="ReturnOrder" variant="archive" /></TabsContent>
        <TabsContent value="del"><DeletedInvoices /></TabsContent>
      </Tabs>
    </div>
  );
}

function PostedShipments() {
  const { rowProps } = useFocusHighlight();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [undoDoc, setUndoDoc] = useState(null);
  const { data: shipments = [] } = useQuery({ queryKey: ["posted-shipments"], queryFn: async () => (await api.get("/posted-shipments")).data });
  const undo = useMutation({
    mutationFn: async (sid) => (await api.post(`/posted-shipments/${sid}/undo`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["posted-shipments"] });
      qc.invalidateQueries({ queryKey: ["sales-orders"] });
      qc.invalidateQueries({ queryKey: ["items"] });
      toast.success("Shipment undone — inventory restored");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  return (
    <>
      <DataTable
        testid="posted-shipments-table"
        data={shipments}
        rowKey={(s) => s.id}
        rowTestid="shipment-row"
        rowProps={(s) => { const rp = rowProps(s.id); return { ...rp, onClick: () => navigate(`/sales/posted-shipment/${s.id}`), className: `cursor-pointer ${rp.className || ""}` }; }}
        empty="No posted shipments yet."
        columns={[
          { key: "shipment_no", header: "Shipment No.", filter: "text", cellClassName: "font-mono text-xs font-medium" },
          { key: "invoice_no", header: "Invoice", filter: "text", cellClassName: "font-mono text-xs" },
          { key: "external_doc_no", header: "External Doc No.", filter: "text", cellClassName: "text-sm text-muted-foreground", cell: (s) => s.external_doc_no || "—" },
          { key: "customer_name", header: "Customer", filter: "text", cellClassName: "text-sm font-medium" },
          { key: "date", header: "Date", filter: "text", cellClassName: "text-sm text-muted-foreground font-mono" },
          { key: "lines", header: "Lines", align: "right", numeric: true, cellClassName: "tabular text-sm", accessor: (s) => (s.lines || []).length, cell: (s) => (s.lines || []).length },
          { key: "actions", header: "Actions", align: "right", sortable: false, cell: (s) => (
            <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => window.open(`${API}/posted-shipments/${s.id}/packing-slip`, "_blank")} data-testid="delivery-note" title="Print delivery note" className="p-1.5 text-muted-foreground hover:text-primary transition-colors"><FilePdf size={16} weight="bold" /></button>
              {(s.order_no || s.source_order_no) && (
                <button onClick={() => setUndoDoc(s)} data-testid="undo-shipment" title="Undo shipment (restore inventory)" className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"><ArrowUUpLeft size={16} weight="bold" /></button>
              )}
            </div>
          ) },
        ]}
      />
      <ConfirmDialog
        open={!!undoDoc}
        onOpenChange={(v) => { if (!v) setUndoDoc(null); }}
        title="Undo this shipment?"
        description={`${undoDoc?.shipment_no} will be reversed: the shipped quantities return to stock and its sales order reopens. This is blocked if the quantities have already been invoiced.`}
        confirmLabel="Undo Shipment"
        testid="undo-shipment-confirm"
        onConfirm={() => { undo.mutate(undoDoc.id); setUndoDoc(null); }}
      />
    </>
  );
}

function PostedReturnReceipts() {
  const navigate = useNavigate();
  const { data: receipts = [] } = useQuery({ queryKey: ["posted-return-receipts"], queryFn: async () => (await api.get("/posted-return-receipts")).data });
  return (
    <DataTable
      testid="posted-return-receipts-table"
      data={receipts}
      rowKey={(r) => r.id}
      rowTestid="return-receipt-row"
      rowProps={(r) => ({ onClick: () => navigate(`/sales/posted-return-receipt/${r.id}`), className: "cursor-pointer" })}
      empty="No posted return receipts yet."
      columns={[
        { key: "receipt_no", header: "Receipt No.", filter: "text", cellClassName: "font-mono text-xs font-medium" },
        { key: "order_no", header: "Return Order", filter: "text", cellClassName: "font-mono text-xs" },
        { key: "customer_name", header: "Customer", filter: "text", cellClassName: "text-sm font-medium" },
        { key: "date", header: "Date", filter: "text", cellClassName: "text-sm text-muted-foreground font-mono" },
        { key: "total", header: "Credited", align: "right", numeric: true, cell: (r) => <Money value={r.total} className="text-sm font-medium" /> },
      ]}
    />
  );
}
